/**
 * Reading a Canvas Switch row.
 *
 * A Switch is identified by its serial number rather than by an id: that is
 * what the Switch Dashboard route carries, what the row click stores, and
 * what is printed on the box. Name and MAC are the other two ways a request
 * can name one.
 */

/**
 * Return a Switch row identifier.
 * @param {object} item Canvas row
 * @return {*} identifier
 */
export function switchId(item) {
	return item?.serial_no ?? item?.serialNo ?? item?.id;
}

/**
 * Normalize a Canvas Switch row into an agent candidate.
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
export function normalizeSwitchReference(item) {
	const id = switchId(item);
	const name = item?.device_name ?? item?.switchName ?? item?.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'switch',
		entity_id: String(id),
		name: String(name),
		serial_no: id == null ? null : String(id),
		site_id: item?.site_id ?? null,
		site_name: item?.site_name ?? null,
		status: item?.status == null ? null : String(item.status),
	};
}

/**
 * Build the minimum Switch row the dashboard route needs.
 *
 * A switch reached by looking one up arrives with the whole Canvas row
 * retained; one reached from the page the user is already standing on never
 * was looked up, so nothing was retained and the row has to be made from
 * what the command carries. The Access Point route has had this from the
 * start; the Switch did not, and every dashboard action taken while already
 * on that switch's page failed with "Canvas could not open the Switch
 * inventory" - the page names the switch in its own route and would have
 * fetched it, but the state builder threw before it could.
 *
 * @param {object} params navigation command parameters
 * @return {object|null} Canvas route row
 */
export function buildSwitchRouteRow(params = {}) {
	const id = params.entity_id ?? params.serial_no;
	if (id == null || !String(id).trim()) return null;
	const name = params.entity_name ?? params.device_name;
	return {
		serial_no: String(id).trim(),
		device_name: name && String(name).trim() ? String(name).trim() : null,
	};
}
